// Правки строк доски: имя и цвет, новая дорожка или трек, удаление, порядок ручкой (дорожки между собой,
// треки — и между дорожками). Что записать — planner; здесь только вызовы и присутствие.
import { confirmDialog } from '@core/ui/confirm';
import { useCallback, useRef, type RefObject } from 'react';
import { data } from '@core/store';
import { applyPlanned } from '../roadmap/shared/apply';
import { useRowReorder } from '../roadmap/shared/interaction';
import { planRowSet, planRowsReorder, planSwimlaneInsert, planTrackInsert, type Board, type RowTable } from './planner';

type Opts = { gridRef: RefObject<HTMLDivElement | null>; launchId: string | null; board: Board; fail: (err?: string) => void };

export function useLaunchRows({ gridRef, launchId, board, fail }: Opts) {
  const latest = useRef(board);
  latest.current = board;

  const setRow = useCallback(async (table: RowTable, id: string, set: { name?: string; color?: string }) => {
    const row = data.store.getState().tables[table].get(id);
    if (!row) return;
    const r = await data.applyChanges(planRowSet(table, row, set), { kind: set.color ? 'recolor_launch_swimlane' : 'rename_launch_row', id });
    if (!r.ok) fail(r.error);
  }, [fail]);
  const rename = useCallback((table: RowTable, id: string, name: string) => setRow(table, id, { name }), [setRow]);
  const recolor = useCallback((id: string, color: string) => {
    if (data.store.getState().tables.launch_swimlane.get(id)?.color === color) return; // пикер закрыли без изменения
    return setRow('launch_swimlane', id, { color });
  }, [setRow]);

  const addSwimlane = useCallback(async () => {
    if (!launchId) return;
    const r = await data.applyChanges(planSwimlaneInsert(launchId, latest.current.lanes), { kind: 'create_launch_swimlane', launch_id: launchId });
    if (!r.ok) fail(r.error);
  }, [launchId, fail]);
  const addTrack = useCallback(async (swimlaneId: string) => {
    const r = await data.applyChanges(planTrackInsert(swimlaneId, latest.current.tracks), { kind: 'create_launch_track', swimlane_id: swimlaneId });
    if (!r.ok) fail(r.error);
  }, [fail]);
  const deleteRow = useCallback(async (table: RowTable, id: string, label: string, count: number) => {
    if (!(await confirmDialog({ title: 'Delete row', text: `Delete «${label}»${count ? ` with its ${count} task(s)` : ''}? They stay in the history and can be restored later.`, yes: 'Delete', danger: true }))) return;
    const r = await data.applyChanges([{ table, id, delete: true }], { kind: 'delete_launch_row', id });
    if (!r.ok) fail(r.error);
  }, [fail]);

  const trackLane = (trackId: string) => latest.current.tracks.find((t) => t.id === trackId)?.swimlane_id ?? null;
  const laneReorder = useRowReorder({
    gridRef, rowAttr: 'data-launch-row',
    targetIdOf: (attr) => (attr.startsWith('s:') ? attr.slice(2) : trackLane(attr.slice(2))),
    blockSelector: (id) => `[data-launch-row="s:${id}"], [data-launch-row][data-swimlane="${id}"]`,
    visibleIds: () => latest.current.lanes.map((s) => s.id),
    onDrop: (fromId, anchorId) => void applyPlanned(() => ({ changes: planRowsReorder('launch_swimlane', latest.current.lanes, fromId, anchorId), op: { kind: 'reorder_launch_swimlanes' } })).then((r) => { if (!r.ok) fail(r.error); }),
  });
  const trackReorder = useRowReorder({
    gridRef, rowAttr: 'data-launch-row',
    targetIdOf: (attr) => (attr.startsWith('t:') ? attr.slice(2) : null),
    blockSelector: (id) => `[data-launch-row="t:${id}"]`,
    visibleIds: () => latest.current.trackOrder,
    onDrop: (fromId, anchorId) => {
      const { lanes, tracks } = latest.current;
      const lane = anchorId ? trackLane(anchorId) : (lanes[lanes.length - 1]?.id ?? null);
      void applyPlanned(() => ({ changes: planRowsReorder('launch_track', tracks, fromId, anchorId, lane), op: { kind: 'reorder_launch_tracks' } })).then((r) => { if (!r.ok) fail(r.error); });
    },
  });

  return { rename, recolor, addSwimlane, addTrack, deleteRow, laneReorder, trackReorder };
}
